import React, { Component } from 'react';
import Data from '../../data';
import UI from '../../ui';
import Utils from '../../utils';
import AddCarrierHeader from '../AddCarrierHeader.component';
import FedExEulaAgreement from './FedExEulaAgreement.component';

const { Communication, Util, withLayoutAndModalContexts } = Utils;
const { WrappedInputTextWithLabel, Footer, Section, SectionBody } = UI;
const { Carrier } = Data;

class AddFedExAccount extends Component {
	constructor(props) {
		super(props);

		this.state = {
			fedExAccountNumber: '',
			firstName: '',
			lastName: '',
			company: '',
			address1: '',
			address2: '',
			city: '',
			state: '',
			zip: '',
			phone: '',
			email: '',
			validations: {
				fedExAccountNumber: 0,
				firstName: 0,
				lastName: 0,
				address1: 0,
				city: 0,
				state: 0,
				zip: 0,
				phone: 0,
				email: 0,
			},
			isLoading: false,
			acceptedEula: false,
		};

		// For Enterprise, we pass the custId
		if (props.custId) {
			this.state.custId = props.custId;
		}
	}

	generateSubtitle = () => (
		<p>
			Complete the form below to link your FedEx account. If you do not already have a FedEx
			account, you can create one by{' '}
			<a
				href="https://www.fedex.com/en-us/create-account.html"
				target="_blank"
				rel="noreferrer noopener"
				data-qa="fedex-account-sign-up"
				{...{ dataqa: 'fedex-account-sign-up' }}
			>
				clicking here
			</a>
			.
		</p>
	);

	validateAccount = () => {
		const { validations } = this.state;
		const newValidations = { ...validations };
		let validated = true;

		// check for invalid fields
		Object.keys(newValidations).map(key => {
			if (newValidations[key] === -1) {
				validated = false;
			}

			// invalidate untouched fields
			if (newValidations[key] === 0) {
				newValidations[key] = -1;
				validated = false;
			}

			return key;
		});

		this.setState({
			validations: newValidations,
		});

		return validated;
	};

	onChangeInputText = ({ target: { name, value } }) => {
		if (name === 'phone') {
			const formattedPhone = Util.formatPhoneText(value);
			this.setState({ phone: formattedPhone });
		} else if (name === 'zip' || name === 'upsAccountZip') {
			const formattedZip = Util.formatZip(value);
			this.setState({ [name]: formattedZip });
		} else {
			this.setState({ [name]: value });
		}
	};

	onBlurInputText = ({ target: { name, value } }) => {
		this.setState(({ validations }) => ({
			validations: {
				...validations,
				[name]: value !== '' ? 1 : -1,
			},
		}));
	};

	onClickConnect = async () => {
		const {
			completionCallback,
			modalContext: { showError },
		} = this.props;

		const {
			fedExAccountNumber,
			firstName,
			lastName,
			company,
			address1,
			address2,
			city,
			state,
			zip,
			phone,
			email,
			custId = null,
		} = this.state;

		const params = {
			UserOwnedAccount: true,
			Carrier: 'FedEx',
			AccountNumber: fedExAccountNumber,
			AccountCountry: 'US',
			AccountZipCode: zip,
			AgreeToEula: true,
			NegotiatedRates: false,
			DeviceIdentity: '',
			Address: {
				FullName: `${firstName} ${lastName}`,
				Company: company,
				Address1: address1,
				Address2: address2,
				City: city,
				State: state,
				ZIPCode: zip.slice(0, 5),
				ZIPCodeAddOn: zip.slice(6, 10),
				PhoneNumber: phone,
				EmailAddress: email,
			},
		};

		let response;

		this.setState({
			isLoading: true,
		});

		if (this.validateAccount()) {
			if (custId) {
				response = await Communication.addCarrierToLocation(params, custId);
			} else {
				response = await Communication.addCarrier(params);
			}

			this.setState({
				isLoading: false,
			});

			if (Communication.isSuccessful(response)) {
				completionCallback(true, Carrier.FedEx);
			} else {
				showError({ response });
			}
		}

		this.setState({
			isLoading: false,
		});
	};

	onClickCancel = () => {
		const { completionCallback } = this.props;

		completionCallback(false);
	};

	onAcceptEula = accepted => {
		if (accepted) {
			this.setState({
				acceptedEula: true,
			});
		}
	};

	render() {
		const {
			fedExAccountNumber,
			firstName,
			lastName,
			company,
			address1,
			address2,
			city,
			state,
			zip,
			phone,
			email,
			validations,
			isLoading,
			acceptedEula,
		} = this.state;

		const {
			layoutContext: { isMobile, isPhoneLayout },
			completionCallback,
			enterpriseLocations,
		} = this.props;

		const mobileCls = isPhoneLayout ? 'mobile' : '';

		if (!acceptedEula) {
			return (
				<FedExEulaAgreement
					completionCallback={completionCallback}
					onAcceptEula={this.onAcceptEula}
					enterpriseLocations={enterpriseLocations}
				/>
			);
		}

		return (
			<div className="common-carrier-add-fedex-account">
				<Section>
					<AddCarrierHeader
						className={isMobile ? 'mobile' : ''}
						title="Account Details"
						carrier={Carrier.FedEx}
						subtitle={this.generateSubtitle()}
						isMobile={isMobile}
					/>
					<SectionBody dataqa="fedex-account-details-body-wrapper">
						<div className="p-grid p-dir-col form-container">
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.fedExAccountNumber === -1}
								errorMessage="FedEx Account # is required."
								label="FedEx Account #:"
								uiElementConfig={{
									value: fedExAccountNumber,
									name: 'fedExAccountNumber',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									maxLength: 1000,
									dataqa: 'fedex-account-number',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.firstName === -1}
								errorMessage="First Name is required."
								label="First Name:"
								uiElementConfig={{
									value: firstName,
									name: 'firstName',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									maxLength: 1000,
									dataqa: 'first-name',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.lastName === -1}
								errorMessage="Last Name is required."
								label="Last Name:"
								uiElementConfig={{
									value: lastName,
									name: 'lastName',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									maxLength: 1000,
									dataqa: 'last-name',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								label="Company:"
								uiElementConfig={{
									value: company,
									name: 'company',
									onChange: this.onChangeInputText,
									maxLength: 1000,
									dataqa: 'company-name',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.address1 === -1}
								errorMessage="Address is required."
								label="Address:"
								uiElementConfig={{
									value: address1,
									name: 'address1',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									placeholder: 'Line 1',
									maxLength: 1000,
									dataqa: 'address-line1',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								label=" "
								uiElementConfig={{
									value: address2,
									name: 'address2',
									onChange: this.onChangeInputText,
									placeholder: 'Line 2',
									maxLength: 1000,
									dataqa: 'address-line2',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.city === -1}
								errorMessage="City is required."
								label="City:"
								uiElementConfig={{
									value: city,
									name: 'city',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									maxLength: 1000,
									dataqa: 'city',
								}}
							/>
							<div className={`p-col-12 p-md-6 p-lg-6 row-wrapper ${mobileCls}`}>
								<WrappedInputTextWithLabel
									className="p-col-12 p-md-6 p-lg-5 state-input"
									showErrorBelow={isMobile}
									isInputInvalid={validations.state === -1}
									errorMessage="State is required."
									label="State:"
									uiElementConfig={{
										value: state,
										name: 'state',
										onChange: this.onChangeInputText,
										onBlur: this.onBlurInputText,
										required: true,
										maxLength: 2,
										dataqa: 'state',
									}}
								/>
								<WrappedInputTextWithLabel
									className="p-col-12 p-md-6 p-lg-7 zip-input"
									showErrorBelow={isMobile}
									isInputInvalid={validations.zip === -1}
									errorMessage="ZIP code is required."
									label="ZIP:"
									uiElementConfig={{
										value: zip,
										name: 'zip',
										onChange: this.onChangeInputText,
										onBlur: this.onBlurInputText,
										required: true,
										maxLength: 10,
										dataqa: 'zip-code',
									}}
								/>
							</div>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.phone === -1}
								errorMessage="Phone number is required."
								label="Phone:"
								uiElementConfig={{
									value: phone,
									name: 'phone',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									placeholder: 'XXX-XXX-XXXX',
									maxLength: 1000,
									required: true,
									dataqa: 'phone',
								}}
							/>
							<WrappedInputTextWithLabel
								className="p-col-12 p-md-6 p-lg-6"
								showErrorBelow={isMobile}
								isInputInvalid={validations.email === -1}
								errorMessage="Email address is required."
								label="Email:"
								uiElementConfig={{
									type: 'email',
									value: email,
									name: 'email',
									onChange: this.onChangeInputText,
									onBlur: this.onBlurInputText,
									required: true,
									maxLength: 255,
									dataqa: 'email',
								}}
							/>
						</div>
					</SectionBody>
					<Footer
						className={`fedex-footer ${mobileCls}`}
						primary={{
							label: 'Connect FedEx Account',
							onClick: this.onClickConnect,
							disabled: isLoading,
							dataqa: 'fedex-account-details-primary-btn',
						}}
						secondary={{
							label: enterpriseLocations ? 'Start Over' : 'Cancel',
							onClick: this.onClickCancel,
							dataqa: 'fedex-account-details-secondary-btn',
						}}
					>
						<p
							data-qa="fedex-account-details-legal-copy"
							{...{ dataqa: 'fedex-account-details-legal-copy' }}
						>
							FedEx service marks are owned by Federal Express Corporation and used by
							permission.
						</p>
					</Footer>
				</Section>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(AddFedExAccount);
